import { ServiceEndpoints } from "../../constants/ServiceEndpoints";
import type {
  CreateOrderPayload,
  GetOrdersPayload,
  Order,
  RespondToOrderPayload,
} from "../../interfaces/OrderObject";
import {
  convertRawOrderEnvelope,
  convertRawOrderList,
  extractOrderError,
  orderEnvelopeHasError,
} from "../converters/backendToUi/orderConvertor";
import {
  buildCreateOrderPayload,
  buildGetOrdersPayload,
  buildRespondToOrderPayload,
} from "../converters/uiToBackend/OrderConvertor";
import { postRequest } from "../http/httpRequest";

/** Parses the JSON envelope and throws a clean Error if it signals failure. */
async function readEnvelope(response: Response): Promise<unknown> {
  const text = await response.text().catch(() => "");
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  // A non-JSON body means the API route isn't responding with our envelope
  // (e.g. an SPA index.html 404 fallback). Surface a clear message instead of
  // a confusing parse failure.
  if (data === null) {
    throw new Error("Ordering isn't available right now. Please try again later.");
  }
  if (!response.ok || orderEnvelopeHasError(data)) {
    throw new Error(extractOrderError(data));
  }
  return data;
}

async function createOrder(payload: CreateOrderPayload): Promise<Order> {
  const response = await postRequest(
    ServiceEndpoints.ORDER_CREATE,
    buildCreateOrderPayload(payload),
    true
  );
  const data = await readEnvelope(response);
  const order = convertRawOrderEnvelope(data);
  if (!order) throw new Error("Order was created but could not be read back.");
  return order;
}

async function getOrdersByBusinessId(payload: GetOrdersPayload): Promise<Order[]> {
  const response = await postRequest(
    ServiceEndpoints.ORDER_GET_BY_BUSINESS,
    buildGetOrdersPayload(payload),
    true
  );
  const data = await readEnvelope(response);
  return convertRawOrderList(data);
}

async function respondToOrder(payload: RespondToOrderPayload): Promise<Order> {
  const response = await postRequest(
    ServiceEndpoints.ORDER_RESPOND,
    buildRespondToOrderPayload(payload),
    true
  );
  const data = await readEnvelope(response);
  const order = convertRawOrderEnvelope(data);
  if (!order) throw new Error("Order was updated but could not be read back.");
  return order;
}

const OrderApiService = {
  createOrder,
  getOrdersByBusinessId,
  respondToOrder,
};

export default OrderApiService;
